import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PageHeader from './PageHeader';
import PreviewRoiOverlay from './PreviewRoiOverlay';
import ConfiguredMsePlayer from './ConfiguredMsePlayer';
import ROIDrawer from './ROIDrawer';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { formatDateWithSeconds } from '../utils/dateUtils';
import {
  getCameraStreams,
  getConfiguredCameraLivePreviewMseUrl,
  getROIConfig,
  getWorkstationCameraPreviewJpegUrl,
  getWorkstationCameraPreviewMjpegUrl,
  getWorkstationCameraSnapshot,
  getWorkstationCameras,
  assignWorkstationCamera,
  unassignWorkstationCamera,
  getWorkstationOccupancySummary,
  saveROIConfig,
} from '../utils/api';
import { getOrderedCameraStreams, pumpMjpegStream } from '../utils/mjpegPreview';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
const WORKSTATION_TYPE = 'WORKSTATION';
const DOT_PALETTE = ['#14b8a6', '#4f46e5', '#94a3b8', '#f59e0b', '#ef4444'];
const LIVE_PREVIEW_MAX_RECONNECT = 5;
/** Give Detect enough time to publish the first annotated workstation frame. */
const LIVE_PREVIEW_PLAY_TIMEOUT_MS = 45000;
const PREVIEW_ZOOM_MIN = 1;
const PREVIEW_ZOOM_MAX = 4;
const PREVIEW_ZOOM_STEP = 0.25;

const clampPreviewPan = (pan, zoom, viewportEl) => {
  if (zoom <= 1 || !viewportEl) return { x: 0, y: 0 };
  const vw = viewportEl.clientWidth || 0;
  const vh = viewportEl.clientHeight || 0;
  if (!vw || !vh) return pan;
  const maxX = Math.max(0, (vw * (zoom - 1)) / 2);
  const maxY = Math.max(0, (vh * (zoom - 1)) / 2);
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)),
    y: Math.min(maxY, Math.max(-maxY, pan.y)),
  };
};

const getStreamUrls = (camera) => {
  if (Array.isArray(camera?.stream_urls) && camera.stream_urls.length) {
    return camera.stream_urls.filter(Boolean);
  }
  if (Array.isArray(camera?.streams) && camera.streams.length) {
    return camera.streams.map((s) => s?.stream_url).filter(Boolean);
  }
  return camera?.rtsp_url ? [camera.rtsp_url] : [];
};

/** Primary RTSP stream — prefers workstation assignment, then Detect subtype=0 / camera.rtsp_url. */
const resolveWorkstationStream = (camera) => {
  const streams = getOrderedCameraStreams(camera);
  const assignedStreamId = String(camera?.workstation_stream_id || '').trim();
  if (assignedStreamId) {
    const byId = streams.find((s) => String(s.streamId || '') === assignedStreamId);
    if (byId?.streamUrl) {
      return { streamId: assignedStreamId, streamUrl: byId.streamUrl };
    }
  }
  const primary = (camera?.rtsp_url || '').trim();
  if (primary) {
    const match = streams.find((s) => s.streamUrl === primary);
    return {
      streamId: assignedStreamId || match?.streamId || null,
      streamUrl: primary,
    };
  }
  const main =
    streams.find((s) => /subtype=0(?:\D|$)/.test(s.streamUrl || '')) || streams[0];
  if (main?.streamUrl) return main;
  const fallback = getStreamUrls(camera)[0];
  return fallback ? { streamId: assignedStreamId || null, streamUrl: fallback } : null;
};

const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/** Local calendar day as YYYY-MM-DD (browser timezone). */
const todayLocalDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatWorkstationLabel = (workstationId) => {
  const text = String(workstationId ?? '').trim();
  if (!text) return '—';
  const stripped = text.replace(/^(workstation|desk|workspace)[\s_-]*/i, '').trim() || text;
  return `Workstation-${stripped}`;
};

const formatClock = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * The summary poll lags the on-screen clock by about a poll interval.
 * Corrections inside this window are ignored so the label keeps +1s steps
 * instead of snapping backward and then jumping two seconds.
 */
const PREVIEW_DURATION_DRIFT_SEC = 5;

const floorDurationSec = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
};

const previewDurationShown = (anchor, now) => {
  const elapsed = Math.max(0, Math.floor((now - anchor.anchorMs) / 1000));
  return {
    occupiedSec: anchor.occupiedBase + (anchor.occupied ? elapsed : 0),
    emptySec: anchor.emptyBase == null ? null : anchor.emptyBase + (anchor.occupied ? 0 : elapsed),
    sessionSec: anchor.occupied && anchor.sessionBase != null ? anchor.sessionBase + elapsed : null,
  };
};

const durationsClose = (shown, server) =>
  shown == null || server == null || Math.abs(shown - server) <= PREVIEW_DURATION_DRIFT_SEC;

/** Local 1-second timeline per desk. Server samples only correct large drift. */
const reconcilePreviewDurationAnchor = (anchors, key, row, now) => {
  const occupied = String(row.status || '').toLowerCase() === 'occupied';
  const serverOccupied = floorDurationSec(row.total_duration_sec) ?? 0;
  const serverEmpty = floorDurationSec(row.empty_duration_sec);
  const serverSession = floorDurationSec(row.open_duration_sec);
  const prev = anchors[key];
  if (!prev) {
    anchors[key] = {
      anchorMs: now,
      occupiedBase: serverOccupied,
      emptyBase: serverEmpty,
      sessionBase: occupied ? serverSession : null,
      occupied,
    };
    return;
  }
  const shown = previewDurationShown(prev, now);
  const occupiedClose = durationsClose(shown.occupiedSec, serverOccupied);
  const emptyClose = durationsClose(shown.emptySec, serverEmpty);
  const sessionClose = !occupied || durationsClose(shown.sessionSec, serverSession);
  const statusChanged = prev.occupied !== occupied;
  if (!statusChanged && occupiedClose && emptyClose && sessionClose) return;

  anchors[key] = {
    anchorMs: now,
    occupiedBase: !statusChanged && occupiedClose ? shown.occupiedSec : serverOccupied,
    emptyBase: serverEmpty == null
      ? null
      : (!statusChanged && emptyClose ? shown.emptySec : serverEmpty),
    sessionBase: !occupied
      ? null
      : (!statusChanged && sessionClose ? shown.sessionSec : serverSession),
    occupied,
  };
};

const WorkstationMonitoring = () => {
  const { confirm } = useConfirmDialog();

  const [loading, setLoading] = useState(true);
  const [streams, setStreams] = useState([]);
  const [assignedCameraIds, setAssignedCameraIds] = useState([]);
  const [assignmentByCameraId, setAssignmentByCameraId] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [selectedStreamId, setSelectedStreamId] = useState('');
  const [selectedStreamUrl, setSelectedStreamUrl] = useState('');

  const [manageMenu, setManageMenu] = useState({ anchorEl: null, camera: null });

  const [roiOpen, setRoiOpen] = useState(false);
  const [roiCamera, setRoiCamera] = useState(null);
  const [roiFrameImage, setRoiFrameImage] = useState(null);
  const [roiExisting, setRoiExisting] = useState(null);
  const [roiExistingLaneRois, setRoiExistingLaneRois] = useState({});
  const [roiExistingOverlapRatios, setRoiExistingOverlapRatios] = useState({});
  const [roiLaneCount, setRoiLaneCount] = useState(0);
  const [roiFetching, setRoiFetching] = useState(false);
  const [roiSaving, setRoiSaving] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCamera, setPreviewCamera] = useState(null);
  const [previewMode, setPreviewMode] = useState('occupancy');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewMseUrl, setPreviewMseUrl] = useState(null);
  const [previewMjpegUrl, setPreviewMjpegUrl] = useState(null);
  const [previewSessionKey, setPreviewSessionKey] = useState(0);
  const [previewHasFrame, setPreviewHasFrame] = useState(false);
  const [previewFallbackImage, setPreviewFallbackImage] = useState(null);
  const [previewLaneRois, setPreviewLaneRois] = useState({});
  const [previewLaneCount, setPreviewLaneCount] = useState(0);
  const [previewRoi, setPreviewRoi] = useState(null);
  const [previewSourceFrameSize, setPreviewSourceFrameSize] = useState(null);
  const [previewOccupancyItems, setPreviewOccupancyItems] = useState([]);
  const [previewClock, setPreviewClock] = useState(() => Date.now());
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });

  const previewMediaHostRef = useRef(null);
  const previewMjpegImgRef = useRef(null);
  const previewFallbackImgRef = useRef(null);
  const previewOpenRef = useRef(false);
  const previewModeRef = useRef('occupancy');
  const previewReconnectAttemptRef = useRef(0);
  const previewReconnectTimerRef = useRef(null);
  const previewPlayTimeoutRef = useRef(null);
  const previewMjpegAbortRef = useRef(null);
  const previewHasFrameRef = useRef(false);
  const restartLivePreviewRef = useRef(null);
  const previewZoomViewportRef = useRef(null);
  const previewDragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const previewDurationAnchorRef = useRef({});

  const resetPreviewZoom = useCallback(() => {
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  }, []);

  const clampPreviewZoom = (value) =>
    Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, Number(value) || 1));

  const handlePreviewZoomBy = useCallback((delta) => {
    setPreviewZoom((prev) => {
      const next = clampPreviewZoom(prev + delta);
      if (next <= 1) {
        setPreviewPan({ x: 0, y: 0 });
      } else {
        setPreviewPan((pan) => clampPreviewPan(pan, next, previewZoomViewportRef.current));
      }
      return next;
    });
  }, []);

  const handlePreviewPointerDown = useCallback(
    (event) => {
      if (previewZoom <= 1 || event.button !== 0) return;
      previewDragRef.current = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        originX: previewPan.x,
        originY: previewPan.y,
        suppressClick: false,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [previewZoom, previewPan.x, previewPan.y],
  );

  const handlePreviewPointerMove = useCallback((event) => {
    const drag = previewDragRef.current;
    if (!drag.active) return;
    const viewport = previewZoomViewportRef.current;
    setPreviewPan(
      clampPreviewPan(
        {
          x: drag.originX + (event.clientX - drag.startX),
          y: drag.originY + (event.clientY - drag.startY),
        },
        previewZoom,
        viewport,
      ),
    );
  }, [previewZoom]);

  const handlePreviewPointerUp = useCallback((event) => {
    if (!previewDragRef.current.active) return;
    const drag = previewDragRef.current;
    const moved = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    drag.active = false;
    drag.suppressClick = moved > 6;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handlePreviewFrameClick = useCallback(
    (event) => {
      if (previewDragRef.current.suppressClick) {
        previewDragRef.current.suppressClick = false;
        return;
      }
      // Zoom-in on click at 1x (replaces global ImageGalleryDialog for this preview).
      if (previewZoom > 1) return;
      event.preventDefault();
      handlePreviewZoomBy(PREVIEW_ZOOM_STEP);
    },
    [previewZoom, handlePreviewZoomBy],
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const clearPreviewReconnectTimer = () => {
    if (previewReconnectTimerRef.current) {
      clearTimeout(previewReconnectTimerRef.current);
      previewReconnectTimerRef.current = null;
    }
  };

  const clearPreviewPlayTimeout = () => {
    if (previewPlayTimeoutRef.current) {
      clearTimeout(previewPlayTimeoutRef.current);
      previewPlayTimeoutRef.current = null;
    }
  };

  const stopLivePreviewSession = useCallback(() => {
    clearPreviewReconnectTimer();
    clearPreviewPlayTimeout();
    try {
      previewMjpegAbortRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    previewMjpegAbortRef.current = null;
  }, []);

  const closePreview = useCallback(() => {
    previewOpenRef.current = false;
    stopLivePreviewSession();
    setPreviewOpen(false);
    setPreviewCamera(null);
    setPreviewMseUrl(null);
    setPreviewMjpegUrl(null);
    previewHasFrameRef.current = false;
    setPreviewHasFrame(false);
    setPreviewFallbackImage(null);
    setPreviewLoading(false);
    setPreviewError('');
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewRoi(null);
    setPreviewSourceFrameSize(null);
    setPreviewOccupancyItems([]);
    previewDurationAnchorRef.current = {};
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
    previewReconnectAttemptRef.current = 0;
  }, [stopLivePreviewSession]);

  const previewWorkstationMeta = useMemo(() => {
    const map = {};
    previewOccupancyItems.forEach((row) => {
      const key = String(row.workstation_id ?? '').trim();
      if (!key) return;
      const occupied = String(row.status || '').toLowerCase() === 'occupied';
      const anchor = previewDurationAnchorRef.current[key];
      const shown = anchor
        ? previewDurationShown(anchor, previewClock)
        : {
            occupiedSec: floorDurationSec(row.total_duration_sec) ?? 0,
            emptySec: floorDurationSec(row.empty_duration_sec),
            sessionSec: occupied ? floorDurationSec(row.open_duration_sec) : null,
          };
      const { occupiedSec, emptySec, sessionSec } = shown;
      const title = formatWorkstationLabel(key);
      map[key] = {
        occupied,
        infoLines: [
          `${title}: ${occupied ? 'Occupied' : 'Idle'}`,
          `First visit: ${formatClock(row.first_occupied_at)}`,
          `Last seen: ${formatClock(row.last_activity_at)}`,
          `Occupied total: ${formatDuration(occupiedSec)}`,
          `Empty total: ${emptySec == null ? '—' : formatDuration(emptySec)}`,
          `Current session: ${sessionSec == null ? '—' : formatDuration(sessionSec)}`,
        ],
      };
    });
    return map;
  }, [previewOccupancyItems, previewClock]);

  const loadPreviewRoiOverlay = useCallback(async (camera) => {
    if (!camera?.id) return;
    try {
      const rv = await getROIConfig(WORKSTATION_TYPE, { cameraId: camera.id });
      if (!previewOpenRef.current) return;
      setPreviewRoi(rv?.roi || null);
      setPreviewLaneRois(rv?.lane_rois || {});
      setPreviewLaneCount(Number(rv?.number_of_lane) || 0);
      const width = Number(rv?.frame_width) || 0;
      const height = Number(rv?.frame_height) || 0;
      setPreviewSourceFrameSize(
        width > 0 && height > 0 ? { width, height } : { width: 1920, height: 1080 },
      );
    } catch (_) {
      if (!previewOpenRef.current) return;
      setPreviewRoi(null);
      setPreviewLaneRois({});
      setPreviewLaneCount(0);
      setPreviewSourceFrameSize(null);
    }
  }, []);

  const fallBackToDetectSnapshot = useCallback(async (camera) => {
    if (!camera?.id || !previewOpenRef.current) return;
    try {
      const detectSnapshotUrl = getWorkstationCameraPreviewJpegUrl(camera.id, {
        cacheKey: Date.now(),
      });
      await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = detectSnapshotUrl;
      });
      if (!previewOpenRef.current || previewHasFrameRef.current) return;
      setPreviewFallbackImage(detectSnapshotUrl);
      setPreviewError('Live stream unavailable — showing the latest annotated frame.');
      setPreviewLoading(false);
      return;
    } catch (err) {
      if (!previewOpenRef.current || previewHasFrameRef.current) return;
      setPreviewError(err?.message || 'Detect annotated preview unavailable');
      setPreviewLoading(false);
    }
  }, []);

  const fallBackToHealthSnapshot = useCallback(async (camera) => {
    if (!camera?.id || !previewOpenRef.current) return;
    try {
      const stream = resolveWorkstationStream(camera);
      const result = await getWorkstationCameraSnapshot(camera.id, {
        streamId: stream?.streamId || undefined,
        streamUrl: stream?.streamUrl || undefined,
      });
      if (!previewOpenRef.current) return;
      if (result?.image) {
        setPreviewMjpegUrl(null);
        setPreviewFallbackImage(result.image);
        setPreviewError('Live stream unavailable — showing a single snapshot instead.');
        setPreviewLoading(false);
      } else {
        setPreviewError(result?.message || 'Preview unavailable');
        setPreviewLoading(false);
      }
    } catch (err) {
      if (!previewOpenRef.current) return;
      setPreviewError(err.message || 'Preview unavailable');
      setPreviewLoading(false);
    }
  }, []);

  const startDetectLivePreview = useCallback((camera) => {
    if (!camera?.id) {
      setPreviewError('Camera not found');
      setPreviewLoading(false);
      return;
    }
    const mjpegUrl = getWorkstationCameraPreviewMjpegUrl(camera.id, {
      cacheKey: Date.now(),
    });
    setPreviewError('');
    previewHasFrameRef.current = false;
    setPreviewHasFrame(false);
    // Keep a still frame on screen while a newly added camera waits for Detect
    // to open its annotated stream.
    setPreviewLoading(true);
    setPreviewMseUrl(null);
    setPreviewMjpegUrl(mjpegUrl);
    setPreviewSessionKey((k) => k + 1);
    clearPreviewPlayTimeout();
    previewPlayTimeoutRef.current = setTimeout(() => {
      if (!previewOpenRef.current) return;
      void fallBackToDetectSnapshot(camera);
    }, LIVE_PREVIEW_PLAY_TIMEOUT_MS);
  }, [fallBackToDetectSnapshot]);

  const startOccupancyPreview = useCallback((camera) => {
    const stream = resolveWorkstationStream(camera);
    if (!camera?.id) {
      setPreviewError('Camera not found');
      setPreviewLoading(false);
      return;
    }
    if (!stream?.streamUrl) {
      setPreviewError('No stream URL available for preview');
      setPreviewLoading(false);
      return;
    }
    const mseUrl = getConfiguredCameraLivePreviewMseUrl(camera.id, {
      streamUrl: stream.streamUrl,
      streamId: stream.streamId || undefined,
    });
    setPreviewError('');
    previewHasFrameRef.current = false;
    setPreviewHasFrame(false);
    setPreviewFallbackImage(null);
    setPreviewLoading(true);
    setPreviewMjpegUrl(null);
    setPreviewMseUrl(mseUrl);
    setPreviewSessionKey((k) => k + 1);
    clearPreviewPlayTimeout();
    previewPlayTimeoutRef.current = setTimeout(() => {
      if (!previewOpenRef.current) return;
      void fallBackToHealthSnapshot(camera);
    }, LIVE_PREVIEW_PLAY_TIMEOUT_MS);
  }, [fallBackToHealthSnapshot]);

  const startCurrentPreview = useCallback(
    (camera) => {
      if (previewModeRef.current === 'detect') {
        startDetectLivePreview(camera);
      } else {
        startOccupancyPreview(camera);
      }
    },
    [startDetectLivePreview, startOccupancyPreview],
  );

  restartLivePreviewRef.current = () => {
    if (!previewOpenRef.current || !previewCamera) return;
    startCurrentPreview(previewCamera);
  };

  const handlePreviewPlaying = useCallback(() => {
    previewReconnectAttemptRef.current = 0;
    previewHasFrameRef.current = true;
    clearPreviewPlayTimeout();
    setPreviewHasFrame((prev) => prev || true);
    setPreviewLoading((prev) => (prev ? false : prev));
    setPreviewError((prev) => (prev ? '' : prev));
  }, []);

  const handlePreviewStreamError = useCallback(() => {
    if (!previewOpenRef.current) return;
    const attempt = previewReconnectAttemptRef.current;
    if (attempt >= LIVE_PREVIEW_MAX_RECONNECT) {
      clearPreviewPlayTimeout();
      setPreviewLoading(false);
      previewReconnectAttemptRef.current = 0;
      if (previewModeRef.current === 'detect') {
        void fallBackToDetectSnapshot(previewCamera);
      } else {
        void fallBackToHealthSnapshot(previewCamera);
      }
      // Detect restarts its graph after a camera is added. Keep polling so the
      // dialog fills in once that camera starts publishing frames.
      clearPreviewReconnectTimer();
      previewReconnectTimerRef.current = setTimeout(() => {
        if (!previewOpenRef.current) return;
        restartLivePreviewRef.current?.();
      }, 8000);
      return;
    }
    previewReconnectAttemptRef.current = attempt + 1;
    const delayMs = Math.min(4000, 600 * 2 ** attempt);
    clearPreviewReconnectTimer();
    previewReconnectTimerRef.current = setTimeout(() => {
      if (!previewOpenRef.current) return;
      restartLivePreviewRef.current?.();
    }, delayMs);
  }, [fallBackToDetectSnapshot, fallBackToHealthSnapshot, previewCamera]);

  useEffect(
    () => () => {
      previewOpenRef.current = false;
      clearPreviewReconnectTimer();
      clearPreviewPlayTimeout();
      try {
        previewMjpegAbortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
    },
    [],
  );

  // Chrome does not paint a chunked multipart MJPEG inside this dialog (the
  // zoom wrapper always has a CSS transform, and load/error never fire), so
  // the spinner stays up even though Detect is publishing frames. Pull the
  // stream and swap in each JPEG instead.
  useEffect(() => {
    if (!previewOpen || previewMode !== 'detect' || !previewMjpegUrl) return undefined;
    const controller = new AbortController();
    previewMjpegAbortRef.current = controller;
    let sawFrame = false;
    pumpMjpegStream({
      url: previewMjpegUrl,
      imgRef: previewMjpegImgRef,
      signal: controller.signal,
      onFrame: () => {
        if (!previewOpenRef.current) return;
        if (sawFrame) return;
        sawFrame = true;
        previewHasFrameRef.current = true;
        setPreviewFallbackImage(null);
        handlePreviewPlaying();
      },
      onError: () => {
        if (!previewOpenRef.current || controller.signal.aborted) return;
        handlePreviewStreamError();
      },
      onEnded: () => {
        if (!previewOpenRef.current || controller.signal.aborted) return;
        handlePreviewStreamError();
      },
    });
    return () => {
      controller.abort();
      if (previewMjpegAbortRef.current === controller) {
        previewMjpegAbortRef.current = null;
      }
    };
  }, [previewOpen, previewMode, previewMjpegUrl, handlePreviewPlaying, handlePreviewStreamError]);

  const loadStreams = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCameraStreams();
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      setStreams(list);
    } catch (err) {
      showSnackbar(err.message || 'Failed to load cameras', 'error');
      setStreams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkstationCameras = useCallback(async () => {
    const applyRows = (rows) => {
      const ids = rows.map((c) => c.camera_id).filter(Boolean);
      const byId = {};
      rows.forEach((row) => {
        const cid = String(row?.camera_id || '').trim();
        if (!cid) return;
        byId[cid] = {
          stream_url: row.stream_url || '',
          stream_id: row.stream_id || '',
          created_at: row.created_at || row.first_assigned_at || null,
          created_by: row.created_by || null,
          created_by_name: row.created_by_name || null,
          roi_updated_at: row.roi_updated_at || null,
          roi_last_updated_by: row.roi_last_updated_by || null,
          roi_last_updated_by_name: row.roi_last_updated_by_name || null,
        };
      });
      setAssignmentByCameraId(byId);
      setAssignedCameraIds(ids.map(String));
    };

    try {
      const data = await getWorkstationCameras();
      const rows = Array.isArray(data?.cameras)
        ? data.cameras
        : Array.isArray(data?.camera_ids)
          ? data.camera_ids.map((id) => ({ camera_id: id }))
          : [];
      applyRows(rows);
      return;
    } catch (_) {
      // Fall through to legacy camera_type=WORKSTATION when API unavailable.
    }

    try {
      const all = await getCameraStreams();
      const list = Array.isArray(all?.items)
        ? all.items
        : Array.isArray(all?.cameras)
          ? all.cameras
          : Array.isArray(all)
            ? all
            : [];
      const legacy = list.filter(
        (c) => String(c?.camera_type || '').trim().toUpperCase() === WORKSTATION_TYPE,
      );
      applyRows(
        legacy.map((c) => ({
          camera_id: c.id,
          stream_url: c.rtsp_url || getStreamUrls(c)[0] || '',
          stream_id: '',
        })),
      );
    } catch (_) {
      setAssignmentByCameraId({});
      setAssignedCameraIds([]);
    }
  }, []);

  useEffect(() => {
    loadStreams();
    loadWorkstationCameras();
  }, [loadStreams, loadWorkstationCameras]);

  // Live preview: poll occupancy every 2s for status and labels. Durations tick
  // from a local 1s clock so a poll cannot jump the totals forward or backward.
  useEffect(() => {
    previewDurationAnchorRef.current = {};
    if (!previewOpen || previewMode !== 'occupancy' || !previewCamera?.id) {
      setPreviewOccupancyItems([]);
      return undefined;
    }
    let cancelled = false;
    let clockTimer = null;
    const tickClock = () => {
      const now = Date.now();
      setPreviewClock(now);
      const delay = 1000 - (now % 1000);
      clockTimer = setTimeout(tickClock, delay > 0 ? delay : 1000);
    };
    const loadPreviewOccupancy = async () => {
      try {
        const data = await getWorkstationOccupancySummary({
          cameraId: previewCamera.id,
          date: todayLocalDate(),
        });
        if (cancelled) return;
        const now = Date.now();
        const items = Array.isArray(data?.items) ? data.items : [];
        const anchors = previewDurationAnchorRef.current;
        const seen = new Set();
        items.forEach((row) => {
          const key = String(row.workstation_id ?? '').trim();
          if (!key) return;
          seen.add(key);
          reconcilePreviewDurationAnchor(anchors, key, row, now);
        });
        Object.keys(anchors).forEach((key) => {
          if (!seen.has(key)) delete anchors[key];
        });
        setPreviewOccupancyItems(items);
      } catch (_) {
        if (!cancelled) setPreviewOccupancyItems([]);
      }
    };
    loadPreviewOccupancy();
    const timer = setInterval(loadPreviewOccupancy, 2000);
    tickClock();
    return () => {
      cancelled = true;
      clearInterval(timer);
      if (clockTimer) clearTimeout(clockTimer);
    };
  }, [previewOpen, previewMode, previewCamera?.id]);

  // Mouse wheel zoom on live preview (ROI overlay scales with frame).
  useEffect(() => {
    if (!previewOpen) return undefined;
    const node = previewZoomViewportRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      handlePreviewZoomBy(event.deltaY < 0 ? PREVIEW_ZOOM_STEP : -PREVIEW_ZOOM_STEP);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [previewOpen, previewHasFrame, previewFallbackImage, handlePreviewZoomBy]);

  const assignedIdSet = useMemo(() => new Set(assignedCameraIds), [assignedCameraIds]);

  const workstationCameras = useMemo(
    () =>
      streams
        .filter((c) => c?.id && assignedIdSet.has(String(c.id)))
        .map((camera) => {
          const assignment = assignmentByCameraId[String(camera.id)];
          if (!assignment) return camera;
          return {
            ...camera,
            ...(assignment.stream_url
              ? {
                  rtsp_url: assignment.stream_url,
                  workstation_stream_id: assignment.stream_id || null,
                }
              : {}),
            workstation_created_at: assignment.created_at || null,
            workstation_created_by: assignment.created_by || null,
            workstation_created_by_name: assignment.created_by_name || null,
            workstation_roi_updated_at: assignment.roi_updated_at || null,
            workstation_roi_updated_by: assignment.roi_last_updated_by || null,
            workstation_roi_updated_by_name: assignment.roi_last_updated_by_name || null,
          };
        }),
    [streams, assignedIdSet, assignmentByCameraId],
  );

  const selectableCameras = useMemo(
    () => streams.filter((c) => c?.id && !assignedIdSet.has(String(c.id))),
    [streams, assignedIdSet],
  );

  const filteredSelectableCameras = useMemo(() => {
    const q = String(assignSearch || '').trim().toLowerCase();
    if (!q) return selectableCameras;
    return selectableCameras.filter((camera) => {
      const haystack = [
        camera.label,
        camera.id,
        camera.description,
        camera.camera_type,
        ...(Array.isArray(camera.stream_urls) ? camera.stream_urls : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [selectableCameras, assignSearch]);

  const getCameraStreamOptions = (camera) => {
    if (Array.isArray(camera?.streams) && camera.streams.length) {
      return camera.streams
        .map((s) => ({
          streamId: String(s.id || s.stream_id || '').trim(),
          streamUrl: String(s.stream_url || s.streamUrl || '').trim(),
        }))
        .filter((s) => s.streamUrl);
    }
    return getStreamUrls(camera).map((url) => ({ streamId: '', streamUrl: url }));
  };

  const handleOpenAssign = () => {
    setAssignError('');
    setAssignSearch('');
    setSelectedCameraId('');
    setSelectedStreamId('');
    setSelectedStreamUrl('');
    setAssignOpen(true);
  };

  const selectCameraForAssign = (camera) => {
    if (!camera?.id) return;
    const streamOptions = getCameraStreamOptions(camera);
    const defaultStream = streamOptions[0];
    setSelectedCameraId(camera.id);
    setSelectedStreamId(defaultStream?.streamId || '');
    setSelectedStreamUrl(defaultStream?.streamUrl || '');
  };

  const selectStreamForAssign = (camera, stream) => {
    if (!camera?.id || !stream?.streamUrl) return;
    setSelectedCameraId(camera.id);
    setSelectedStreamId(stream.streamId || '');
    setSelectedStreamUrl(stream.streamUrl);
  };

  const handleDeAssign = async (camera) => {
    const approved = await confirm({
      title: 'Remove from monitoring',
      message: `Remove "${camera.label || camera.id}" from Workstation Monitoring? ROI and occupancy history are kept.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!approved) return;
    try {
      await unassignWorkstationCamera(camera.id);
      showSnackbar('Camera removed from Workstation Monitoring');
      await loadWorkstationCameras();
    } catch (err) {
      showSnackbar(err.message || 'Failed to remove camera', 'error');
    }
  };

  const openPreview = (camera, mode = 'occupancy') => {
    if (previewOpenRef.current) {
      stopLivePreviewSession();
    }
    clearPreviewReconnectTimer();
    previewReconnectAttemptRef.current = 0;
    previewOpenRef.current = true;
    previewModeRef.current = mode;
    setPreviewMode(mode);
    setPreviewCamera(camera);
    setPreviewOpen(true);
    setPreviewError('');
    previewHasFrameRef.current = false;
    setPreviewHasFrame(false);
    setPreviewFallbackImage(null);
    setPreviewMseUrl(null);
    setPreviewMjpegUrl(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewSourceFrameSize(null);
    resetPreviewZoom();
    if (mode === 'occupancy') {
      void loadPreviewRoiOverlay(camera);
      startOccupancyPreview(camera);
    } else {
      void loadPreviewRoiOverlay(camera);
      startDetectLivePreview(camera);
    }
  };

  const handleAssignPreview = (camera, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!camera) return;
    openPreview(camera);
  };

  const loadRoiFrameForCamera = async (camera) => {
    const stream = resolveWorkstationStream(camera);
    const [healthResult, roiResult] = await Promise.allSettled([
      getWorkstationCameraSnapshot(camera.id, {
        streamId: stream?.streamId || undefined,
        streamUrl: stream?.streamUrl || undefined,
      }),
      getROIConfig(WORKSTATION_TYPE, { cameraId: camera.id }),
    ]);

    if (healthResult.status === 'fulfilled' && healthResult.value?.image) {
      setRoiFrameImage(healthResult.value.image);
    } else {
      setRoiFrameImage(null);
      const msg =
        healthResult.status === 'fulfilled'
          ? healthResult.value?.message
          : healthResult.reason?.message;
      showSnackbar(msg || 'Could not fetch camera frame', 'warning');
    }

    if (roiResult.status === 'fulfilled' && roiResult.value) {
      const rv = roiResult.value;
      setRoiExisting(rv.roi || null);
      setRoiExistingLaneRois(rv.lane_rois || {});
      setRoiExistingOverlapRatios(
        rv.lane_overlap_ratios && typeof rv.lane_overlap_ratios === 'object'
          ? rv.lane_overlap_ratios
          : {},
      );
      setRoiLaneCount(Number(rv.number_of_lane) || 0);
    } else {
      setRoiExisting(null);
      setRoiExistingLaneRois({});
      setRoiExistingOverlapRatios({});
      setRoiLaneCount(0);
    }
  };

  const handleOpenRoi = async (camera) => {
    setManageMenu({ anchorEl: null, camera: null });
    setRoiCamera(camera);
    setRoiOpen(true);
    setRoiFetching(true);
    try {
      await loadRoiFrameForCamera(camera);
    } finally {
      setRoiFetching(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCameraId || !selectedStreamUrl) {
      setAssignError('Select a camera and stream');
      return;
    }
    setAssignLoading(true);
    setAssignError('');
    const cameraId = selectedCameraId;
    const streamId = selectedStreamId;
    const streamUrl = selectedStreamUrl;
    const baseCamera = streams.find((c) => String(c.id) === String(cameraId));
    const cameraForRoi = {
      ...(baseCamera || { id: cameraId, label: cameraId }),
      rtsp_url: streamUrl,
      workstation_stream_id: streamId || null,
    };
    try {
      await assignWorkstationCamera({
        cameraId,
        streamId: streamId || undefined,
        streamUrl,
      });
      showSnackbar('Camera added to Workstation Monitoring');
      setAssignOpen(false);
      await Promise.all([loadWorkstationCameras(), loadStreams()]);
      // Open Draw ROI immediately for the newly added camera.
      await handleOpenRoi(cameraForRoi);
    } catch (err) {
      setAssignError(err.message || 'Failed to assign camera');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSaveLaneCount = async (_type, laneValue) => {
    const count = Math.max(0, parseInt(laneValue, 10) || 0);
    if (!roiCamera?.id) {
      showSnackbar('Select a camera before setting workstation count', 'error');
      throw new Error('camera required');
    }
    try {
      const result = await saveROIConfig(WORKSTATION_TYPE, {
        camera_id: roiCamera.id,
        number_of_lane: count,
        ...(count === 0 ? { lane_rois: {} } : {}),
      });
      setRoiLaneCount(Number(result?.number_of_lane ?? count));
      if (count === 0) {
        setRoiExistingLaneRois({});
        setRoiExistingOverlapRatios({});
      } else if (result?.lane_rois) {
        setRoiExistingLaneRois(result.lane_rois);
      }
      showSnackbar(`Workstation count set to ${count}`);
      await loadWorkstationCameras();
    } catch (err) {
      showSnackbar(err.message || 'Failed to save workstation count', 'error');
      throw err;
    }
  };

  const handleSaveRoi = async (roiData) => {
    if (!roiCamera?.id) {
      showSnackbar('Select a camera before saving ROI', 'error');
      return;
    }
    setRoiSaving(true);
    try {
      const rect = roiData.rect || [roiData.x, roiData.y, roiData.w, roiData.h];
      const stream = resolveWorkstationStream(roiCamera);
      const roiPayload = {
        camera_id: roiCamera.id,
        rect,
        number_of_lane: roiData.number_of_lane ?? roiLaneCount,
        lane_rois: roiData.lane_rois || {},
        frame_width: roiData.frame_width,
        frame_height: roiData.frame_height,
        stream_url: stream?.streamUrl || undefined,
      };
      if (Object.prototype.hasOwnProperty.call(roiData, 'lane_overlap_ratios')) {
        roiPayload.lane_overlap_ratios = roiData.lane_overlap_ratios;
      }
      await saveROIConfig(WORKSTATION_TYPE, roiPayload);
      showSnackbar('Workstation ROI saved (synced to Detect when available)');
      setRoiOpen(false);
      await Promise.all([loadStreams(), loadWorkstationCameras()]);
    } catch (err) {
      showSnackbar(err.message || 'Failed to save ROI', 'error');
    } finally {
      setRoiSaving(false);
    }
  };

  const renderCameraRow = (camera, index) => {
    const dot = DOT_PALETTE[index % DOT_PALETTE.length];
    return (
      <TableRow key={camera.id} hover>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot }} />
            <Link
              component={RouterLink}
              to={`/workstation-monitoring/workspaces/${encodeURIComponent(camera.camera_id ?? camera.id)}`}
              underline="hover"
              variant="body2"
              title="Open workspace"
              sx={{ fontWeight: 600 }}
            >
              {camera.label || camera.id}
            </Link>
            {camera.camera_type &&
            String(camera.camera_type).toUpperCase() !== WORKSTATION_TYPE ? (
              <Chip size="small" label={camera.camera_type} variant="outlined" color="default" />
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
            {camera.description || '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {camera.workstation_created_by_name || camera.workstation_created_by || '—'}
            {camera.workstation_created_at
              ? `, ${formatDateWithSeconds(camera.workstation_created_at)}`
              : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {camera.workstation_roi_updated_by_name || camera.workstation_roi_updated_by || '—'}
            {camera.workstation_roi_updated_at
              ? `, ${formatDateWithSeconds(camera.workstation_roi_updated_at)}`
              : ''}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => setManageMenu({ anchorEl: e.currentTarget, camera })}
            sx={{ textTransform: 'none' }}
          >
            Manage
          </Button>
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="contained"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => openPreview(camera, 'detect')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Live-preview
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  const refreshAll = async () => {
    await Promise.all([loadStreams(), loadWorkstationCameras()]);
  };

  return (
    <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, sm: 1 } }}>
      <PageHeader
        title="Workstation Monitoring"
        subtitle="Select cameras from Camera Configuration, draw workstation ROIs, and track occupancy duration"
        icon={DesktopWindowsIcon}
      />

      {/* ROI Setup — assigned cameras from Camera Configuration pool */}
      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ROI Setup &amp; Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign cameras from your configured pool, then draw one ROI per workstation (Number of workstations).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleOpenAssign}
              disabled={loading || selectableCameras.length === 0}
            >
              Add Camera
            </Button>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={refreshAll}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        {loading ? (
          <LinearProgress />
        ) : workstationCameras.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No cameras assigned yet. Add cameras on Camera &amp; Configuration, then click Add Camera to select them here.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell align="right">Manage</TableCell>
                  <TableCell align="right">Live Preview</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workstationCameras.map((camera, index) => renderCameraRow(camera, index))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Manage menu — icons aligned with Camera Configuration ROI manage menu */}
      <Menu
        anchorEl={manageMenu.anchorEl}
        open={Boolean(manageMenu.anchorEl)}
        onClose={() => setManageMenu({ anchorEl: null, camera: null })}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <MenuItem
          onClick={() => {
            if (manageMenu.camera) openPreview(manageMenu.camera);
            setManageMenu({ anchorEl: null, camera: null });
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Preview" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (manageMenu.camera) handleOpenRoi(manageMenu.camera);
          }}
        >
          <ListItemIcon>
            <CropFreeIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Mark Workstations" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const cam = manageMenu.camera;
            setManageMenu({ anchorEl: null, camera: null });
            if (cam) handleDeAssign(cam);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="De-Assign" />
        </MenuItem>
      </Menu>

      {/* Assign camera from Camera Configuration pool */}
      <Dialog open={assignOpen} onClose={() => !assignLoading && setAssignOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Add Camera for Monitoring
          </Typography>
          <IconButton onClick={() => !assignLoading && setAssignOpen(false)} size="small" disabled={assignLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {assignError ? <Alert severity="error">{assignError}</Alert> : null}

            {selectableCameras.length > 0 ? (
              <TextField
                size="small"
                fullWidth
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                placeholder="Search workspace / camera…"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            ) : null}

            {selectableCameras.length === 0 ? (
              <Alert severity="info">
                All configured cameras are already assigned. Add more on{' '}
                <Link component={RouterLink} to="/camera-streams" underline="hover">
                  Camera &amp; Configuration
                </Link>
                .
              </Alert>
            ) : filteredSelectableCameras.length === 0 ? (
              <Alert severity="info">No cameras match “{assignSearch.trim()}”.</Alert>
            ) : (
              <RadioGroup
                value={selectedCameraId}
                onChange={(e) => {
                  const camera = filteredSelectableCameras.find(
                    (c) => String(c.id) === e.target.value,
                  );
                  if (camera) selectCameraForAssign(camera);
                }}
              >
                {filteredSelectableCameras.map((camera) => {
                const streamOptions = getCameraStreamOptions(camera);
                const selected = selectedCameraId === camera.id;
                return (
                  <Box
                    key={camera.id}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor: selected ? 'action.hover' : 'background.paper',
                      mb: 2,
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Radio
                        value={camera.id}
                        disabled={assignLoading}
                        inputProps={{ 'aria-label': `Select ${camera.label || camera.id}` }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {camera.label || camera.id}
                          </Typography>
                          {camera.camera_type ? (
                            <Chip size="small" label={camera.camera_type} variant="outlined" />
                          ) : (
                            <Chip size="small" label="Unassigned role" variant="outlined" />
                          )}
                        </Stack>
                      </Box>
                      <Tooltip title="Preview">
                        <span>
                          <IconButton
                            color="primary"
                            aria-label={`Preview ${camera.label || camera.id}`}
                            disabled={assignLoading || streamOptions.length === 0}
                            onClick={(e) => handleAssignPreview(camera, e)}
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': { bgcolor: 'primary.dark' },
                              '&.Mui-disabled': {
                                bgcolor: 'action.disabledBackground',
                                color: 'action.disabled',
                              },
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>

                    {streamOptions.length === 0 ? (
                      <Typography variant="caption" color="error" sx={{ pl: 5 }}>
                        No stream URL
                      </Typography>
                    ) : (
                      <Stack spacing={0.25} sx={{ pl: 5, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Available streams
                        </Typography>
                        <RadioGroup
                          value={selected ? selectedStreamUrl : ''}
                          onChange={(e) => {
                            const picked = streamOptions.find((s) => s.streamUrl === e.target.value);
                            if (picked) selectStreamForAssign(camera, picked);
                          }}
                        >
                        {streamOptions.map((stream, idx) => {
                          const streamLabel =
                            streamOptions.length > 1
                              ? `Stream ${idx + 1}`
                              : 'Primary stream';
                          return (
                            <FormControlLabel
                              key={`${camera.id}-${stream.streamId || stream.streamUrl}-${idx}`}
                              value={stream.streamUrl}
                              sx={{
                                alignItems: 'flex-start',
                                mr: 0,
                                ml: 0,
                                '& .MuiFormControlLabel-label': { width: '100%' },
                              }}
                              control={
                                <Radio
                                  size="small"
                                  disabled={assignLoading}
                                />
                              }
                              label={
                                <Box sx={{ pt: 0.75 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {streamLabel}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ wordBreak: 'break-all', display: 'block' }}
                                  >
                                    {stream.streamUrl}
                                  </Typography>
                                </Box>
                              }
                            />
                          );
                        })}
                        </RadioGroup>
                      </Stack>
                    )}
                  </Box>
                );
              })}
              </RadioGroup>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAssignOpen(false)} disabled={assignLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={
              assignLoading ||
              selectableCameras.length === 0 ||
              !selectedCameraId ||
              !selectedStreamUrl
            }
          >
            {assignLoading ? <CircularProgress size={22} /> : 'Add Camera'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Preview = go2rtc + occupancy overlay; Live-preview = Detect MJPEG + ROI canvas. */}
      <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <span>
            {previewCamera?.label || 'Camera Preview'}
            {previewMode === 'detect' ? ' — Detect annotated preview' : ' — Occupancy preview'}
          </span>
          <IconButton onClick={closePreview} size="small" aria-label="Close preview">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewError ? (
            <Alert severity="error" sx={{ mb: previewHasFrame || previewLoading ? 2 : 0 }}>
              {previewError}
            </Alert>
          ) : null}
          <Box sx={{ position: 'relative', minHeight: 240 }}>
            <Box
              ref={previewZoomViewportRef}
              data-no-image-preview="true"
              onPointerDown={handlePreviewPointerDown}
              onPointerMove={handlePreviewPointerMove}
              onPointerUp={handlePreviewPointerUp}
              onPointerCancel={handlePreviewPointerUp}
              onDoubleClick={resetPreviewZoom}
              onClick={handlePreviewFrameClick}
              sx={{
                display:
                  !previewFallbackImage &&
                  (previewMseUrl ||
                    previewMjpegUrl ||
                    previewLoading ||
                    (!previewHasFrame && !previewError))
                    ? 'flex'
                    : 'none',
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.900',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                width: '100%',
                height: '70vh',
                minHeight: 240,
                cursor: previewZoom > 1 ? 'grab' : 'zoom-in',
                touchAction: 'none',
                userSelect: 'none',
                '&:active': {
                  cursor: previewZoom > 1 ? 'grabbing' : 'zoom-in',
                },
              }}
            >
              <Box
                ref={previewMediaHostRef}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                  transformOrigin: 'center center',
                  transition: previewDragRef.current.active ? 'none' : 'transform 0.08s ease-out',
                }}
              >
                {previewMseUrl ? (
                  <ConfiguredMsePlayer
                    key={previewSessionKey}
                    src={previewMseUrl}
                    onPlaying={handlePreviewPlaying}
                    onError={handlePreviewStreamError}
                  />
                ) : null}
                {previewMjpegUrl ? (
                  <img
                    key={previewSessionKey}
                    ref={previewMjpegImgRef}
                    alt={`${previewCamera?.label || 'Workstation camera'} live preview`}
                    data-no-image-preview="true"
                    draggable={false}
                    onLoad={handlePreviewPlaying}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                ) : null}
                {previewMode === 'occupancy' ? (
                  <PreviewRoiOverlay
                    imgRef={previewMediaHostRef}
                    roi={previewRoi}
                    laneRois={previewLaneRois}
                    laneCount={previewLaneCount}
                    zoneNoun="Workstation"
                    active={previewHasFrame}
                    workstationMeta={previewWorkstationMeta}
                  />
                ) : null}
                {previewMode === 'detect' && previewMjpegUrl ? (
                  <PreviewRoiOverlay
                    imgRef={previewMjpegImgRef}
                    roi={previewRoi}
                    laneRois={previewLaneRois}
                    laneCount={previewLaneCount}
                    zoneNoun="Workstation"
                    active={previewHasFrame}
                    workstationMeta={null}
                    sourceFrameSize={previewSourceFrameSize}
                  />
                ) : null}
              </Box>
              {!previewHasFrame && !previewError && !previewFallbackImage ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress sx={{ color: '#fff' }} />
                </Box>
              ) : null}
            </Box>
            {!previewHasFrame && previewFallbackImage ? (
              <Box
                ref={previewZoomViewportRef}
                data-no-image-preview="true"
                onPointerDown={handlePreviewPointerDown}
                onPointerMove={handlePreviewPointerMove}
                onPointerUp={handlePreviewPointerUp}
                onPointerCancel={handlePreviewPointerUp}
                onDoubleClick={resetPreviewZoom}
                onClick={handlePreviewFrameClick}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '70vh',
                  minHeight: 240,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  bgcolor: 'grey.900',
                  cursor: previewZoom > 1 ? 'grab' : 'zoom-in',
                  touchAction: 'none',
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    lineHeight: 0,
                    maxWidth: '100%',
                    maxHeight: '100%',
                    transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                    transformOrigin: 'center center',
                  }}
                >
                  <img
                    ref={previewFallbackImgRef}
                    src={previewFallbackImage}
                    alt="snapshot preview"
                    data-no-image-preview="true"
                    draggable={false}
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                  {previewMode === 'occupancy' ? (
                    <PreviewRoiOverlay
                      imgRef={previewFallbackImgRef}
                      roi={previewRoi}
                      laneRois={previewLaneRois}
                      laneCount={previewLaneCount}
                      zoneNoun="Workstation"
                      active
                      workstationMeta={previewWorkstationMeta}
                    />
                  ) : null}
                  {previewMode === 'detect' ? (
                    <PreviewRoiOverlay
                      imgRef={previewFallbackImgRef}
                      roi={previewRoi}
                      laneRois={previewLaneRois}
                      laneCount={previewLaneCount}
                      zoneNoun="Workstation"
                      active
                      workstationMeta={null}
                      sourceFrameSize={previewSourceFrameSize}
                    />
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ position: 'relative', minHeight: 48, px: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%' }}
          >
            <IconButton
              size="small"
              aria-label="Zoom out"
              onClick={() => handlePreviewZoomBy(-PREVIEW_ZOOM_STEP)}
              disabled={previewZoom <= PREVIEW_ZOOM_MIN}
            >
              <RemoveCircleIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => previewCamera && startCurrentPreview(previewCamera)}
              disabled={!previewCamera}
            >
              Refresh stream
            </Button>
            <IconButton
              size="small"
              aria-label="Zoom in"
              onClick={() => handlePreviewZoomBy(PREVIEW_ZOOM_STEP)}
              disabled={previewZoom >= PREVIEW_ZOOM_MAX}
            >
              <AddCircleIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Button
            onClick={closePreview}
            sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ROIDrawer
        open={roiOpen}
        onClose={() => setRoiOpen(false)}
        cameraId={roiCamera?.id}
        cameraType={WORKSTATION_TYPE}
        cameraLabel={roiCamera?.label || 'Workspace'}
        imageUrl={roiFrameImage}
        existingRoi={roiExisting}
        existingLaneRois={roiExistingLaneRois}
        existingLaneOverlapRatios={roiExistingOverlapRatios}
        showPersonOverlap
        laneCount={roiLaneCount}
        onLaneCountSet={handleSaveLaneCount}
        onSave={handleSaveRoi}
        onFetchFrame={async () => {
          if (!roiCamera) return;
          setRoiFetching(true);
          try {
            await loadRoiFrameForCamera(roiCamera);
          } finally {
            setRoiFetching(false);
          }
        }}
        loading={roiFetching || roiSaving}
        zoneNoun="Workstation"
        zoneNounPlural="workstations"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WorkstationMonitoring;
